<template>
  <!------------------------------------------
    This component pulls and displays selected Census data points from the Census API.
  -------------------------------------------->
  <div id="new-congressmember-data">
    <PageTitle title="New Congressmember Data"/>
    <SideMarginsWrapper maxWidth="1200">

      <AboutResource
        :techContact1="{ name: 'Jordan', email: 'laytonj@lsc.gov', department: 'ODGA' }" 
      >
        <div>
          <div>
            This resource is intended to provide a handful of population and income data by state and congressional district as used in GRPA's summary documents about new members of congress. The data are pulled directly from the Census Bureau website.
          </div><br>
          <div id="intro-warning">
            NOTE: This resource provides data for <i>116th</i> congressional districts, NOT for the current (118th) congressional districts. The Census Bureau published 2021 American Community Survey tables for <i>116th</i> districts. For now, please refer instead to <a href="https://www.census.gov/mycd/">My Congressional District</a>, which contains a selection of 2021 ACS data for 118th districts.
          </div><br>
          <div>
            Also note that new congressmembers generally start <i>well before</i> the Census Bureau publishes data about the new congressional districts (fall of the same year at the earliest), so users should be cautious that data for a given district number in a state may be for the previous term of Congress. If the district boundaries did not change then this is not an issue. If unsure, please reach out to ODGA.
          </div>
        </div>
      </AboutResource>
      <Block>
        <h2>Select a state</h2>

        <p class="cd-select">
          <StateSelect 
            @stateSelected="handleStateChange"
            :includePR="true"
            :includeAS="false"
            :includeVI="false"
            :includeMP="false"
            :includeGU="false" 
            :simpleVersion="true"
          />
        </p>
        <p class="cd-select">
          <v-select
            v-model="year"
            :items="years"
            :menu-props="{ maxHeight: '400' }"
            label=""
            hint="Year"
            persistent-hint
          >
          </v-select>
        </p>
        <p class="cd-select" :style="{visibility: firstLoad ? 'hidden' : 'visible'}">
          <v-autocomplete
            v-model="filterDropdown"
            :items="areasForDropdown"
            :menu-props="{ maxHeight: '400' }"
            label=""
            hint="Filter by district"
            persistent-hint
          >
          </v-autocomplete>
        </p>
      </Block>
      <Block>
        <div id="cd-loader" :style="{visibility: loading ? 'visible' : 'hidden'}">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>
        <h2 v-if="dataForDisplay">Results</h2>
        <div v-for="area in dataForDisplay" :key="area.NAME" class="cd-section">
          <h3>{{ area.NAME }}</h3>
          <div class="cd-section-content">
            <div class="cd-section-var">
              <span class="cd-var-name">Total population:</span>{{ area.S1701_C01_001E }}
            </div>
            <div class="cd-section-var">
              <span class="cd-var-name">Population below 125% poverty:</span> {{ area.S1701_C01_039E }} ({{ area.pct_below_125 }}%)
            </div>
            <div class="cd-section-var">
              <span class="cd-var-name">Unemployment rate:</span>{{ area.DP03_0009PE }}%
            </div>
            <div class="cd-section-var">
              <span class="cd-var-name">Median household income:</span>${{ area.DP03_0062E }}
            </div>            
            <div class="my-cd-link" v-if="area.myCongrDistUrl">
              See
              <a :href="area.myCongrDistUrl" target="_blank"
                >My Congressional District</a
              >
              for more data.
            </div>
          </div>
        </div>
      </Block>
    </SideMarginsWrapper>
  </div>
</template>

<script>
import StateSelect from "@/components/select/StateSelect";
import AboutResource from "@/components/common/AboutResource";
import PageTitle from '@/components/common/PageTitle'
import SideMarginsWrapper from '@/components/layout/SideMarginsWrapper'
import Block from '@/components/layout/Block'
import { getCensusData } from "@/js/census_query.js";

export default {
  name: "NewCongressmemberData",
  components: { StateSelect, AboutResource, PageTitle, SideMarginsWrapper, Block },

  data() {
    return {
      selectedState: null,
      year: '2021-1',
      years: [
        {'text': '2021 American Community Survey 1-year estimates', 'value': '2021-1'},
        {'text': '2021 American Community Survey 5-year estimates', 'value': '2021-5'},
        {'text': '2020 American Community Survey 5-year estimates', 'value': '2020-5'},
        {'text': '2019 American Community Survey 1-year estimates', 'value': '2019-1'},
        {'text': '2019 American Community Survey 5-year estimates', 'value': '2019-5'},
        {'text': '2018 American Community Survey 1-year estimates', 'value': '2018-1'},
        {'text': '2018 American Community Survey 5-year estimates', 'value': '2018-5'},
        {'text': '2017 American Community Survey 1-year estimates', 'value': '2017-1'},
        {'text': '2017 American Community Survey 5-year estimates', 'value': '2017-5'},
      ],
      acsSubjectVarsList: [ // The variables from SUBJECT tables to pull from the Census API
        "S1701_C01_001E", //Total population #
        "S1701_C01_039E", //Population below 125% poverty #
      ],
      acsDetailedVarsList: [ // The variables from DETAILED tables to pull from the Census API
      ],
      acsProfileVarsList: [ // The variables from PROFILE tables to pull from the Census API (i.e. table DP03)
        "DP03_0009PE", //Unemployment rate civilian labor force
        "DP03_0062E" //Median household income
      ],
      acsData: null, //Data returned from Census API
      acsDataWithCalcs: null, //Calculated variables added such as percent below 125% poverty. From watcher on acsData.
      filterDropdown: "Show All", //v-model for filter dropdown. All by default.
      loading: false,
      areasForDropdown: [],
      firstLoad: true //Just to hide the filter select menu at the start
    }
  },

  computed: {
    period() {
      if (this.periodToggled === 0) {
        return "1"
      } else if (this.periodToggled === 1) {
        return "5"
      } else {
        return null
      }
    },

    allVarsList() {
      return this.acsSubjectVarsList.concat(this.acsProfileVarsList, this.acsDetailedVarsList)
    },

    dataForDisplay() { //Filtered (potentially) list of acsDataWithCalcs based on the filter by area dropdown
      if (this.filterDropdown !== 'Show All') {
        return this.acsDataWithCalcs.filter(x => x.NAME === this.filterDropdown)
      } else {
        return this.acsDataWithCalcs
      }
    }
  },

  async mounted() {
    // If "state" is provided as URL query parameters
    if (this.$route.query.state) {
      this.selectedState = this.$route.query.state
    }
  },

  methods: {
    /**
     * Pass the necessary arguments to (and calls) the getCensusData function from census_query.js and combine results
     */
    getData: async function () {
      if (!(this.selectedState === null ||this.selectedState === undefined ||this.selectedState === "" ||this.year === null ||this.year === undefined ||this.year === "")) {
        
        this.loading = true

        // Parameters no matter the data fetch        
        let year = this.year.split('-')[0]
        let forGeos = ["*"] //all counties
        let inGeos = [this.selectedState] // i.e. ["45"] means in the state of south carolina
        let forGeoType = "congressional district"
        let inGeoType = "state"

        // Data fetch from 5-year for getting all area names
        let allD
        let oneYearOrFive = "5"
        let vars = ["NAME"]
        let tableType = "detailed"

        let d = await getCensusData( year, oneYearOrFive, tableType, vars, forGeoType, forGeos, inGeoType, inGeos)
        allD = d.sort((a, b) =>
          a["congressional district"].localeCompare(
            b["congressional district"]
          )
        )

        // Add the My Congressional District URL
        allD.forEach(function (x) {
          x[
            "myCongrDistUrl"
          ] = `https://www.census.gov/mycd/?st=${x["state"]}&cd=${x["congressional district"]}`
        });


        // Data fetch for subject tables (by district and for the state)
        // eslint-disable-next-line no-unused-vars
        let subjectD, stateSubjectD, profileD, stateProfileD, detailedD, stateDetailedD, combined = [], stateCombined = [], forAreasDropdown = []
        oneYearOrFive = this.year.split('-')[1]

        if (this.acsSubjectVarsList.length > 0) {
          tableType = "subject"
          vars = JSON.parse(JSON.stringify(this.acsSubjectVarsList))
          subjectD = await getCensusData( year, oneYearOrFive, tableType, vars, forGeoType, forGeos, inGeoType, inGeos)
          stateSubjectD = await getCensusData( year, oneYearOrFive, tableType, vars, 'state', [this.selectedState], null, [])
          combined = combined.concat(subjectD)
          stateCombined = stateCombined.concat(stateSubjectD)
        }
        
        if (this.acsProfileVarsList.length > 0) {
          tableType = "profile"
          vars = JSON.parse(JSON.stringify(this.acsProfileVarsList))
          profileD = await getCensusData( year, oneYearOrFive, tableType, vars, forGeoType, forGeos, inGeoType, inGeos)     
          stateProfileD = await getCensusData( year, oneYearOrFive, tableType, vars, 'state', [this.selectedState], null, [])
          combined = combined.concat(profileD)  
          stateCombined = stateCombined.concat(stateProfileD)   
        }

        if (this.acsDetailedVarsList.length > 0) {
          tableType = "detailed"
          vars = JSON.parse(JSON.stringify(this.acsDetailedVarsList))
          detailedD = await getCensusData( year, oneYearOrFive, tableType, vars, forGeoType, forGeos, inGeoType, inGeos)
          stateDetailedD = await getCensusData( year, oneYearOrFive, tableType, vars, 'state', [this.selectedState], null, [])
          combined = combined.concat(detailedD)  
          stateCombined = stateCombined.concat(stateDetailedD)      
        }        

        // Combine all the data!
        combined.forEach(function(dataObj) { //Add variables to the array of objects with all area names
          allD.forEach(function(mainObj, i) {
            if (dataObj.state === mainObj.state && dataObj['congressional district'] === mainObj['congressional district']) {
              allD[i] = Object.assign({}, allD[i], dataObj)
            }

            // While we're at it, save the names to the forAreasDropdown array
            forAreasDropdown.push({'text': mainObj.NAME, 'value': mainObj.NAME})
          })          
        })

        // And add the state data to the front
        allD.unshift({ 'NAME': 'Statewide'})
        stateCombined.forEach(function(dataObj) {
          allD[0] = Object.assign({}, allD[0], dataObj)
        })

        this.acsData = allD

        // Create an object for a filter dropdown
        forAreasDropdown.unshift({'text': 'Statewide', 'value': 'Statewide'})
        forAreasDropdown.unshift({'text': 'Show All', 'value': 'Show All'})
        this.areasForDropdown = forAreasDropdown

        this.loading = false
        this.firstLoad = false

        this.filterDropdown = 'Show All'

        return;
      }
    },

    handleStateChange (st) {
      this.selectedState = st
    }
  },

  watch: {
    selectedState (to) {
      if (this.$route.query.state !== to) {
        this.$router.push({ path: this.$route.path, query: { state: to }})
      }
      this.getData()
    },
    year () {
      this.getData()
    },
    /**
     * Add whatever custom calculations to the ACS data whenever new data are downloaded
     */
    acsData () {
      const acsWithCals = this.acsData
      const vars = JSON.parse(JSON.stringify(this.allVarsList))

      if (this.acsData !== null && this.acsData !== undefined) {
        acsWithCals.forEach(function (x) {
          if (x["S1701_C01_001E"] !== null && x["S1701_C01_039E"] !== null) {
            x["pct_below_125"] = (
              (x["S1701_C01_039E"] / x["S1701_C01_001E"]) *
              100
            ).toFixed(1)
          } else {
            x["pct_below_125"] = null
          }

          // Add comma separator to all numbers and convert to string
          vars.forEach(function (y) {
            if (y !== "NAME") {
              if (x[y] !== null && x[y] !== undefined) {
                x[y] = x[y].toLocaleString()
              }
            }
          })
        })

        this.acsDataWithCalcs = acsWithCals
      }
    },
  },
}
</script>

<style lang="scss">
@import "@/scss/vars.scss";
#new-congressmember-data {
  padding: 0 1rem;

  #intro-warning {
    color: $header-red;
  }
}
.cd-select {
  max-width: 35rem;
  margin: 0 auto 2rem 0;
}
.cd-section {
  margin-bottom: 3rem;
}
.cd-section-content {
  border-left: 3px solid $header-red;
  padding: 0.5rem 1rem;
}
.cd-section-var {
  font-size: 0.9rem;
  padding: 0.125rem;
}
.cd-var-name {
  color: #3b3b3b;
  padding-right: 0.5rem;
}
.my-cd-link {
  color: #5a5a5a;
  font-size: 0.8rem;
  margin-top: 1rem;
}
#cd-main-container {
  position: relative;
}
#cd-loader {
  max-width: 35rem;
  min-width: 30rem;
  top: -40px;
  display: flex;
  justify-content: center;
}
</style>